import { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

const INPUT = 'rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-3 py-2';
const BUTTON = 'ia-hover-pulse rounded-full border border-[var(--surface-border)] px-4 py-2 disabled:opacity-50';

export default function RoleTagsView() {
    const [tags, setTags] = useState([]);
    const [orgId, setOrgId] = useState(null);
    const [admin, setAdmin] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState('');
    const [order, setOrder] = useState(0);
    const [active, setActive] = useState(true);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [pendingFocus, setPendingFocus] = useState('');
    const [busy, setBusy] = useState(false);
    const fieldRefs = useRef({});
    useEffect(() => {
        if (!pendingFocus) return;
        fieldRefs.current[pendingFocus]?.focus();
        setPendingFocus('');
    }, [pendingFocus]);
    const fieldProps = (field) => ({
        ref: (node) => { fieldRefs.current[field] = node; },
        'aria-invalid': Boolean(fieldErrors[field]),
        'aria-describedby': fieldErrors[field] ? `role-tag-${field}-error` : undefined,
        className: `block ${INPUT}${fieldErrors[field] ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`,
        style: fieldErrors[field] ? { borderColor: '#ef4444', outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined,
    });
    const clearFieldError = (field) => {
        setFieldErrors((current) => {
            if (!current[field]) return current;
            const nextErrors = { ...current };
            delete nextErrors[field];
            return nextErrors;
        });
    };
    const load = useCallback(async () => {
        const [result, org, access] = await Promise.all([
            supabaseClient.from('role_tags').select('id, name, sort_order, active').order('sort_order'),
            supabaseClient.rpc('auth_org_id'), supabaseClient.rpc('is_org_admin')
        ]);
        const issue = result.error || org.error || access.error;
        if (issue) { setError(issue.message); return; }
        setTags(result.data || []); setOrgId(org.data); setAdmin(access.data === true);
    }, []);
    useEffect(() => { load().catch((e) => setError(e.message)); }, [load]);
    async function save(event) {
        event.preventDefault(); setError('');
        const errors = {};
        if (!name.trim()) errors.name = 'Role Tag name is required.';
        if (!String(order).trim() || !Number.isInteger(Number(order))) errors.sort_order = 'Enter a whole-number sort order.';
        setFieldErrors(errors);
        const firstField = Object.keys(errors)[0];
        if (firstField) {
            setPendingFocus(firstField);
            return;
        }
        setBusy(true);
        try {
            const record = { name: name.trim(), sort_order: Number(order), active };
            const result = editing
                ? await supabaseClient.from('role_tags').update(record).eq('id', editing).eq('org_id', orgId)
                : await supabaseClient.from('role_tags').insert({ ...record, org_id: orgId });
            if (result.error) throw result.error;
            setEditing(null); setName(''); setOrder(0); setActive(true); await load();
        } catch (e) { setError(e.message || 'Role Tag could not be saved.'); }
        finally { setBusy(false); }
    }
    return <section className="mt-6 space-y-3 rounded-xl border border-[var(--surface-border)] p-4">
        <h2 className="text-lg font-semibold">Role Tags</h2>
        <p>What people lead or own beyond their Title. Inactive tags stay on historical records but cannot be newly assigned.</p>
        {error && <p role="alert">{error}</p>}
        {tags.map((tag) => <div key={tag.id} className="flex justify-between gap-4">
            <span>{tag.name} · Sort order: {tag.sort_order} · {tag.active ? 'Active' : 'Inactive'}</span>
            {admin && <button className={BUTTON} onClick={() => {
                setFieldErrors({}); setPendingFocus(''); setError('');
                setEditing(tag.id); setName(tag.name); setOrder(tag.sort_order); setActive(tag.active);
            }}>Edit Role Tag</button>}
        </div>)}
        {admin && <form noValidate onSubmit={save} className="flex flex-wrap items-end gap-3">
            <label>Name<input {...fieldProps('name')} value={name} onChange={(e) => { clearFieldError('name'); setName(e.target.value); }} />
                {fieldErrors.name && <span id="role-tag-name-error" className="mt-1 block text-xs font-medium text-red-600" role="alert">{fieldErrors.name}</span>}
            </label>
            <label>Sort order<input {...fieldProps('sort_order')} type="number" step="1" value={order} onChange={(e) => { clearFieldError('sort_order'); setOrder(e.target.value); }} />
                {fieldErrors.sort_order && <span id="role-tag-sort_order-error" className="mt-1 block text-xs font-medium text-red-600" role="alert">{fieldErrors.sort_order}</span>}
            </label>
            <label><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active</label>
            <button className={BUTTON} disabled={busy || !orgId}>{editing ? 'Save Role Tag' : 'Add Role Tag'}</button>
            {editing && <button type="button" className={BUTTON} onClick={() => {
                setFieldErrors({}); setPendingFocus(''); setError('');
                setEditing(null); setName(''); setOrder(0); setActive(true);
            }}>Cancel</button>}
        </form>}
    </section>;
}
